import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Share2, Twitter, Facebook, Link2, Trophy, Medal, Star, Copy, Check, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';
const SHARE_BASE_URL = typeof window !== 'undefined' ? window.location.origin : '';

export default function SocialShare({ childId, childName }) {
  const [achievements, setAchievements] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [copied, setCopied] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [achRes, badgeRes] = await Promise.all([
        axios.get(`${API}/sharing/athlete/${childId}`),
        axios.get(`${API}/sharing/badges/${childId}`)
      ]);
      setAchievements(achRes.data);
      setBadges(badgeRes.data.badges);
    } catch (err) {
      console.error('Failed to load sharing data');
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    if (childId) {
      fetchData();
    }
  }, [childId, fetchData]);

  const detectAchievements = async () => {
    setDetecting(true);
    try {
      const res = await axios.post(`${API}/sharing/auto-detect/${childId}`);
      if (res.data.achievements.length > 0) {
        toast.success(`Found ${res.data.achievements.length} potential achievements!`);
        // Auto-create shareables for detected achievements
        for (const ach of res.data.achievements) {
          await axios.post(`${API}/sharing/create`, {
            child_id: childId,
            achievement_type: ach.type,
            title: ach.title,
            metric_name: ach.metric_name,
            metric_value: ach.metric_value,
            metric_unit: ach.metric_unit,
            previous_value: ach.previous_value
          });
        }
        fetchData();
      } else {
        toast.info('No new achievements detected');
      }
    } catch (err) {
      toast.error('Failed to detect achievements');
    } finally {
      setDetecting(false);
    }
  };

  const shareToTwitter = (achievement) => {
    const text = `🏆 ${childName} just achieved: ${achievement.title}! ${achievement.metric_value ? `${achievement.metric_value}${achievement.metric_unit || ''}` : ''} #BeEliteApp #Achievement`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareToFacebook = (achievement) => {
    const url = `${SHARE_BASE_URL}/share/${achievement.id}`;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  };

  const shareToWhatsApp = (achievement) => {
    const text = `🏆 ${childName} just achieved: ${achievement.title}! ${achievement.metric_value ? `${achievement.metric_value}${achievement.metric_unit || ''}` : ''} Check it out: ${SHARE_BASE_URL}/share/${achievement.id}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const copyLink = (achievement) => {
    const url = `${SHARE_BASE_URL}/share/${achievement.id}`;
    navigator.clipboard.writeText(url);
    setCopied(achievement.id);
    setTimeout(() => setCopied(null), 2000);
    toast.success('Link copied!');
  };

  const getAchievementIcon = (type) => {
    switch (type) {
      case 'personal_best': return <Trophy className="h-5 w-5 text-yellow-500" />;
      case 'milestone': return <Star className="h-5 w-5 text-[#02b5ff]" />;
      case 'badge': return <Medal className="h-5 w-5 text-[#ff025f]" />;
      default: return <Trophy className="h-5 w-5 text-[#02b5ff]" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-[#02b5ff]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="social-share">
      {/* Badges Section */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
          <Medal className="h-5 w-5 text-[#ff025f]" />
          Earned Badges
        </h3>
        {badges.length === 0 ? (
          <p className="text-zinc-500 text-sm">No badges earned yet. Keep training!</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {badges.map((badge, idx) => (
              <div
                key={idx}
                className="bg-zinc-800/50 border border-zinc-700 rounded-xl px-4 py-3 flex items-center gap-3"
                title={badge.description}
              >
                <span className="text-2xl">{badge.icon}</span>
                <div>
                  <p className="text-sm font-semibold text-white">{badge.name}</p>
                  <p className="text-xs text-zinc-500">{badge.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Achievements Section */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Share2 className="h-5 w-5 text-[#02b5ff]" />
            Shareable Achievements
          </h3>
          <Button
            onClick={detectAchievements}
            disabled={detecting}
            className="h-9 px-4 bg-gradient-to-r from-[#ff025f] to-[#dd0050] text-white rounded-full text-sm"
            data-testid="detect-achievements-btn"
          >
            {detecting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trophy className="h-4 w-4 mr-1" />}
            Detect Achievements
          </Button>
        </div>

        {achievements.length === 0 ? (
          <div className="text-center py-6 text-zinc-500">
            <Trophy className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p>No achievements to share yet</p>
            <p className="text-xs mt-1">Click "Detect Achievements" to find shareable moments</p>
          </div>
        ) : (
          <div className="space-y-3">
            {achievements.map((ach) => (
              <div
                key={ach.id}
                className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4"
                data-testid={`achievement-${ach.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {getAchievementIcon(ach.achievement_type)}
                    <div>
                      <h4 className="font-semibold text-white">{ach.title}</h4>
                      {ach.metric_value && (
                        <p className="text-[#02b5ff] text-lg font-bold">
                          {ach.metric_value}{ach.metric_unit}
                          {ach.previous_value && (
                            <span className="text-zinc-500 text-sm font-normal ml-2">
                              (was {ach.previous_value}{ach.metric_unit})
                            </span>
                          )}
                        </p>
                      )}
                      <p className="text-xs text-zinc-500 mt-1">
                        {ach.view_count || 0} views • {new Date(ach.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  {/* Share Buttons */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => shareToTwitter(ach)}
                      className="p-2 text-zinc-400 hover:text-[#1DA1F2] hover:bg-[#1DA1F2]/10 rounded-lg transition-all"
                      title="Share on Twitter"
                    >
                      <Twitter className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => shareToFacebook(ach)}
                      className="p-2 text-zinc-400 hover:text-[#4267B2] hover:bg-[#4267B2]/10 rounded-lg transition-all"
                      title="Share on Facebook"
                    >
                      <Facebook className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => shareToWhatsApp(ach)}
                      className="p-2 text-zinc-400 hover:text-[#25D366] hover:bg-[#25D366]/10 rounded-lg transition-all"
                      title="Share on WhatsApp"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => copyLink(ach)}
                      className="p-2 text-zinc-400 hover:text-[#02b5ff] hover:bg-[#02b5ff]/10 rounded-lg transition-all"
                      title="Copy link"
                    >
                      {copied === ach.id ? <Check className="h-4 w-4 text-green-500" /> : <Link2 className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
